import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, Map, Truck } from 'lucide-react';
import { libelle, nomLisible, texteSur } from '../../lib/libelles';
import { motion } from 'framer-motion';

const MACRO_COLUMNS = [
  { id: 'planned', label: 'Planifié', statuses: ['CREATED'] },
  { id: 'driving', label: 'En route', statuses: ['EN_ROUTE_TO_BORDER', 'EN_ROUTE_TO_LOADING', 'EN_ROUTE_TO_ENTRY_CUSTOMS', 'EN_ROUTE_TO_DELIVERY'] },
  { id: 'customs', label: 'À la douane', statuses: ['AT_CUSTOMS_EXIT', 'CLEARED_CUSTOMS_EXIT', 'AT_CUSTOMS_ENTRY', 'CLEARED_CUSTOMS_ENTRY'] },
  { id: 'loading', label: 'Au chargement ou au dépôt', statuses: ['LOADING', 'ARRIVED_AT_DEPOT'] },
  { id: 'done', label: 'Terminé', statuses: ['DELIVERED', 'RETURNING', 'COMPLETED'] }
];

/**
 * `columns` vient de `GET /transport/kanban`: c'est le moteur de workflow du
 * serveur qui décide des colonnes et de la colonne de chaque trajet. Sans lui,
 * on retombe sur un regroupement par statut fait ici, qui ignore les étapes
 * configurées par la société et ne voit que les trajets déjà chargés.
 */
const TripsKanbanBoard = ({ trips, columns, getTruck, getDriver = () => undefined }) => {
  const navigate = useNavigate();

  const getTripsByMacro = (statuses) => {
    return trips.filter(t => statuses.includes(t.status));
  };

  const resolvedColumns = Array.isArray(columns) && columns.length
    ? columns.map((column) => ({
        id: column.step_id || column.code,
        label: texteSur(column.name, libelle('tripStatus', column.code)),
        trips: column.trips || [],
      }))
    : MACRO_COLUMNS.map((column) => ({
        id: column.id,
        label: column.label,
        trips: getTripsByMacro(column.statuses),
      }));

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 h-[calc(100vh-200px)]">
      {resolvedColumns.map((col) => {
        const columnTrips = col.trips;
        return (
          <div key={col.id} className="flex-shrink-0 w-80 flex flex-col bg-muted/50 rounded-xl border border-border">
            <div className="p-3 border-b border-border sticky top-0 bg-muted/95 backdrop-blur z-10 flex items-center justify-between rounded-t-xl">
              <h3 className="font-semibold text-foreground">{col.label}</h3>
              <Badge variant="secondary" className="bg-secondary text-muted-foreground">
                {columnTrips.length}
              </Badge>
            </div>
            
            <div className="p-3 flex flex-col gap-3 overflow-y-auto">
              {columnTrips.map(trip => {
                const truck = getTruck(trip.truck_id);
                return (
                  <motion.div
                    key={trip.id}
                    layoutId={`trip-card-${trip.id}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card
                      className="cursor-pointer hover:shadow-md transition-shadow border-border hover:border-accent-fuel/50"
                      onClick={() => navigate(`/trips/${trip.id}`)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="matricule-plate text-xs px-2 py-0.5">
                            {nomLisible(truck?.matricule, 'Camion non précisé')}
                          </div>
                          <span className="text-[10px] font-medium px-2 py-1 bg-muted rounded-md text-muted-foreground">
                            {libelle('tripStatus', trip.status)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                          <span>{texteSur(trip.departure_country, 'Départ')}</span>
                          <ArrowRight className="w-3 h-3 text-muted-foreground/70" />
                          <span>{texteSur(trip.destination_country, 'Destination')}</span>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Truck className="w-3 h-3" />
                          Chauffeur : {getDriver(trip.driver_id)?.full_name || (trip.driver_id ? 'assigné' : 'non assigné')}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
              {columnTrips.length === 0 && (
                <div className="flex flex-col items-center justify-center p-6 text-muted-foreground/70">
                  <Map className="w-6 h-6 mb-2 opacity-50" />
                  <span className="text-xs">Aucun trajet</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TripsKanbanBoard;
